import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight, ChevronLeft, ChevronDown, Check, Globe, Share2,
  Building2, Sparkles, Loader2, LayoutDashboard, Calendar, Image,
  Search, X, Upload, MapPin, LogOut
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import ImageUpload from '../../components/ImageUpload';

const API = process.env.REACT_APP_BACKEND_URL;
const ONBOARDING_KEY = 'exhibitor_onboarding_done';

const STEPS = [
  { id: 'welcome',  label: 'Welcome',    icon: Sparkles },
  { id: 'company',  label: 'Company',    icon: Building2 },
  { id: 'shows',    label: 'Shows',      icon: Calendar },
  { id: 'branding', label: 'Branding',   icon: Image },
  { id: 'finish',   label: 'Finish',     icon: Share2 },
];

const INDUSTRY_OPTIONS = [
  'Technology', 'Automotive', 'Healthcare', 'Food & Beverage', 'Construction',
  'Energy', 'Fashion & Retail', 'Aerospace', 'Finance', 'Education',
  'Manufacturing', 'Luxury', 'Cosmetics', 'Agriculture', 'Defense',
];

const PLAN_COUNTRY_LIMITS = {
  FREE:       2,
  GROWTH:     5,
  PREMIUM:    10,
  ENTERPRISE: 15,
};

const inputCls = 'w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white transition-shadow focus:shadow-md';
const labelCls = 'block text-sm font-semibold text-gray-700 mb-1.5';

export default function ExhibitorOnboarding() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const exhibitorMembership = user?.org_memberships?.find(m => m.role?.startsWith('EXHIBITOR'));
  const companyName = exhibitorMembership?.org_name || '';
  const planCode = (exhibitorMembership?.plan_code || 'FREE').toUpperCase();
  const extraLimit = PLAN_COUNTRY_LIMITS[planCode] ?? 2;

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [autoSaved, setAutoSaved] = useState(false);

  // ─── Auto-save per step ───────────────────────────────────────────────────
  const autoSave = async (currentStep) => {
    const token = getToken();
    if (!token) return;
    try {
      let payload = {};
      if (currentStep === 1) {
        // Company step
        payload = { tagline, about, website, social: { website, linkedin, instagram, twitter: '', youtube: '' } };
      } else if (currentStep === 2) {
        // Shows step
        payload = {
          industries: industry,
          upcoming_shows: countries.flatMap(c => {
            const cities = citiesMap[c.code] || [];
            if (cities.length === 0) return [{ show_name: '', city: '', country: c.name, country_code: c.code, dates: '', sqm: 0 }];
            return cities.map(city => ({ show_name: '', city, country: c.name, country_code: c.code, dates: '', sqm: 0 }));
          }),
        };
      } else if (currentStep === 3) {
        // Branding step
        payload = { logo_url: logoUrl, cover_image_url: coverUrl };
      }
      if (Object.keys(payload).length === 0) return;
      await axios.patch(`${API}/api/exhibitor/profile`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setAutoSaved(true);
      setTimeout(() => setAutoSaved(false), 2000);
    } catch {} // Silent fail — user not blocked
  };

  const [tagline, setTagline] = useState('');
  const [about, setAbout] = useState('');
  const [website, setWebsite] = useState('');
  const [industry, setIndustry] = useState([]);
  const [countries, setCountries] = useState([]);
  const [logoUrl, setLogoUrl] = useState('');
  const [coverUrl, setCoverUrl] = useState('');

  // Country search
  const [allCountries, setAllCountries] = useState([]);
  const [countryQuery, setCountryQuery] = useState('');
  const [countryDropdown, setCountryDropdown] = useState([]);
  const searchRef = useRef(null);

  // City selection per country
  const [citiesMap, setCitiesMap] = useState({});       // { code: [cityName, ...] }
  const [cityQueries, setCityQueries] = useState({});   // { code: query }
  const [cityDropdowns, setCityDropdowns] = useState({}); // { code: [cityObj, ...] }
  const [cityLoading, setCityLoading] = useState({});   // { code: bool }
  const [accordionOpen, setAccordionOpen] = useState({}); // { code: bool } — expanded state

  const homeCountryCode = (exhibitorMembership?.country_code || user?.country_code || '').toUpperCase();
  const FREE_CITY_LIMIT = 5; // per additional country

  // Load all cities for a country (for home country auto-select)
  const loadAllCities = async (countryCode) => {
    setCityLoading(prev => ({ ...prev, [countryCode]: true }));
    try {
      const res = await axios.get(`${API}/api/dropdown/countries/${countryCode}/cities`);
      const cityNames = (res.data.items || []).map(c => c.name);
      setCitiesMap(prev => ({ ...prev, [countryCode]: cityNames }));
    } catch {} finally {
      setCityLoading(prev => ({ ...prev, [countryCode]: false }));
    }
  };

  useEffect(() => {
    axios.get(`${API}/api/dropdown/countries?limit=300`).then(r => {
      setAllCountries(r.data?.items || []);
    }).catch(() => {});
  }, []);

  // Load existing profile data on mount
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    axios.get(`${API}/api/exhibitor/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const p = r.data?.profile || {};
        if (p.tagline) setTagline(p.tagline);
        if (p.about) setAbout(p.about);
        if (p.logo_url) setLogoUrl(p.logo_url);
        if (p.cover_image_url) setCoverUrl(p.cover_image_url);
        if (p.social?.website) setWebsite(p.social.website);
        if (p.social?.linkedin) setLinkedin(p.social.linkedin);
        if (p.social?.instagram) setInstagram(p.social.instagram);
        if (Array.isArray(p.industries) && p.industries.length > 0) setIndustry(p.industries);
        // Restore countries + citiesMap from upcoming_shows
        const shows = p.upcoming_shows || [];
        if (shows.length > 0) {
          const seen = new Set();
          const restored = [];
          const cmap = {};
          shows.forEach(s => {
            if (!seen.has(s.country_code)) {
              seen.add(s.country_code);
              restored.push({ code: s.country_code, name: s.country || s.country_code, flag: '' });
              cmap[s.country_code] = [];
            }
            if (s.city) cmap[s.country_code].push(s.city);
          });
          setCountries(restored);
          setCitiesMap(cmap);
        }
      }).catch(() => {});
  // eslint-disable-next-line
  }, []);

  // Auto-add home country and load its cities when allCountries is ready
  useEffect(() => {
    if (!homeCountryCode || !allCountries.length) return;
    setCountries(prev => {
      if (prev.find(c => c.code === homeCountryCode)) return prev;
      const homeCountry = allCountries.find(c => c.code === homeCountryCode);
      if (!homeCountry) return prev;
      loadAllCities(homeCountryCode);
      return [homeCountry, ...prev];
    });
  // eslint-disable-next-line
  }, [homeCountryCode, allCountries]);

  useEffect(() => {
    if (countryQuery.length < 1) { setCountryDropdown([]); return; }
    const q = countryQuery.toLowerCase();
    const results = allCountries
      .filter(c => c.name?.toLowerCase().includes(q))
      .filter(c => !countries.find(sel => sel.code === c.code))
      .slice(0, 8);
    setCountryDropdown(results);
  }, [countryQuery, allCountries, countries]);

  const addCountry = (c) => {
    const isOwn = c.code === homeCountryCode;
    const extras = countries.filter(x => x.code !== homeCountryCode);
    if (!isOwn && extras.length >= extraLimit) return; // plan limit
    if (countries.find(x => x.code === c.code)) return;
    setCountries(prev => [...prev, c]);
    setCountryQuery('');
    setCountryDropdown([]);
  };

  const removeCountry = (code) => {
    setCountries(prev => prev.filter(c => c.code !== code));
    setCitiesMap(prev => { const n = { ...prev }; delete n[code]; return n; });
    setCityQueries(prev => { const n = { ...prev }; delete n[code]; return n; });
    setCityDropdowns(prev => { const n = { ...prev }; delete n[code]; return n; });
  };

  const searchCities = async (countryCode, query) => {
    setCityQueries(prev => ({ ...prev, [countryCode]: query }));
    if (query.length < 1) { setCityDropdowns(prev => ({ ...prev, [countryCode]: [] })); return; }
    setCityLoading(prev => ({ ...prev, [countryCode]: true }));
    try {
      const res = await axios.get(`${API}/api/dropdown/countries/${countryCode}/cities?q=${encodeURIComponent(query)}`);
      const selected = citiesMap[countryCode] || [];
      setCityDropdowns(prev => ({
        ...prev,
        [countryCode]: (res.data.items || []).filter(c => !selected.includes(c.name)).slice(0, 8),
      }));
    } catch {} finally { setCityLoading(prev => ({ ...prev, [countryCode]: false })); }
  };

  const addCity = (countryCode, cityName) => {
    if ((citiesMap[countryCode] || []).includes(cityName)) return;
    setCitiesMap(prev => ({ ...prev, [countryCode]: [...(prev[countryCode] || []), cityName] }));
    setCityQueries(prev => ({ ...prev, [countryCode]: '' }));
    setCityDropdowns(prev => ({ ...prev, [countryCode]: [] }));
  };

  const removeCity = (countryCode, cityName) => {
    setCitiesMap(prev => ({ ...prev, [countryCode]: (prev[countryCode] || []).filter(c => c !== cityName) }));
  };

  const fillTestData = () => {
    setTagline('Global innovator in industrial automation, exhibiting since 1998');
    setAbout('Messe Frankfurt is a leading trade fair organizer operating globally. We represent 100+ brands across manufacturing, technology, and industrial sectors at major venues including Frankfurt, Shanghai, and Las Vegas. Our team specializes in creating premium stand experiences that convert visitor interest into real business outcomes.');
    setWebsite('mf-exhibitions.de');
    setIndustry(['Technology', 'Manufacturing', 'Energy']);
    const testCountries = [
      { code: 'DE', name: 'Germany', flag: '🇩🇪' },
      { code: 'AE', name: 'UAE', flag: '🇦🇪' },
      { code: 'US', name: 'United States', flag: '🇺🇸' },
    ];
    setCountries(testCountries);
    setCitiesMap({
      DE: ['Frankfurt', 'Berlin', 'Munich'],
      AE: ['Dubai', 'Abu Dhabi'],
      US: ['Las Vegas', 'New York', 'Chicago'],
    });
  };
  const [linkedin, setLinkedin] = useState('');
  const [instagram, setInstagram] = useState('');

  const getToken = () => localStorage.getItem('access_token') || sessionStorage.getItem('access_token');

  const skipOnboarding = () => {
    localStorage.setItem(ONBOARDING_KEY, '1');
    localStorage.removeItem(`${ONBOARDING_KEY}_banner_dismissed`);
    navigate('/app/exhibitor/dashboard');
  };

  const handleNext = async () => {
    if (step < STEPS.length - 1) {
      await autoSave(step);
      setStep(s => s + 1);
    } else {
      await handleFinish();
    }
  };

  const handleFinish = async () => {
    setSaving(true); setError('');
    try {
      const token = getToken();
      await axios.patch(`${API}/api/exhibitor/profile`, {
        tagline, about, website, logo_url: logoUrl, cover_image_url: coverUrl,
        industry_tags: industry,
        social: { website, linkedin, instagram, twitter: '', youtube: '' },
        upcoming_shows: countries.flatMap(c => {
          const cities = citiesMap[c.code] || [];
          if (cities.length === 0) {
            return [{ show_name: '', city: '', country: c.name, country_code: c.code, dates: '', sqm: 0 }];
          }
          return cities.map(city => ({
            show_name: '',
            city,
            country: c.name,
            country_code: c.code,
            dates: '',
            sqm: 0,
          }));
        }),
      }, { headers: { Authorization: `Bearer ${token}` } });
      localStorage.setItem(ONBOARDING_KEY, '1');
      localStorage.removeItem(`${ONBOARDING_KEY}_banner_dismissed`);
      navigate('/app/exhibitor/dashboard');
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed. Please try again.');
    } finally { setSaving(false); }
  };

  const progress = ((step + 1) / STEPS.length) * 100;
  const isLastStep = step === STEPS.length - 1;
  const isFirstStep = step === 0;

  const stepWelcome = (
    <div className="text-center space-y-6">
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center mx-auto shadow-xl">
        <Sparkles className="h-10 w-10 text-white" />
      </div>
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-3">Welcome to ExpoStand!</h2>
        <p className="text-gray-500 text-base leading-relaxed max-w-md mx-auto">
          Set up your exhibitor profile to post briefs, receive proposals from top contractors, and showcase your brand at global trade shows.
        </p>
      </div>
      <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-5 border border-indigo-100 max-w-lg mx-auto">
        <p className="text-xs font-bold text-indigo-600 uppercase tracking-wide mb-4">Why complete your profile?</p>
        <div className="grid grid-cols-3 gap-4">
          {[
            { stat: '5×', label: 'more proposals for exhibitors with complete profiles' },
            { stat: '83%', label: 'of contractors check exhibitor profile before bidding' },
            { stat: '24h', label: 'avg. time to first proposal for verified exhibitors' },
          ].map(({ stat, label }) => (
            <div key={stat} className="text-center">
              <p className="text-2xl font-black text-indigo-700">{stat}</p>
              <p className="text-xs text-gray-500 mt-1 leading-snug">{label}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm max-w-lg mx-auto">
        {[
          { icon: Building2, title: 'Brand Your Presence', desc: 'Logo, cover & company info' },
          { icon: Globe, title: 'Show Coverage', desc: 'Where you exhibit globally' },
          { icon: LayoutDashboard, title: 'Post Briefs', desc: 'Receive targeted proposals' },
        ].map(({ icon: Icon, title, desc }) => (
          <div key={title} className="bg-indigo-50 rounded-xl p-4 border border-indigo-100">
            <Icon className="h-5 w-5 text-indigo-600 mx-auto mb-2" />
            <p className="font-semibold text-gray-800 text-xs">{title}</p>
            <p className="text-gray-500 text-xs mt-0.5">{desc}</p>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-400">Takes about 3 minutes · Skip anytime</p>
        <button type="button" onClick={fillTestData}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-indigo-400/50 text-indigo-500 text-xs font-semibold hover:bg-indigo-50 transition-colors">
          ⚡ Fill Test Data
        </button>
      </div>
    </div>
  );

  const stepCompany = (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">About Your Company</h2>
        <p className="text-gray-500 text-sm">Tell contractors who you are so they can tailor their proposals.</p>
      </div>
      <div>
        <label className={labelCls}>Tagline</label>
        <input className={inputCls} value={tagline} onChange={e => setTagline(e.target.value)}
          placeholder="e.g. Global innovator in industrial automation, exhibiting since 1998" />
      </div>
      <div>
        <label className={labelCls}>About <span className="text-gray-400 font-normal">(optional)</span></label>
        <textarea className={`${inputCls} resize-none`} rows={7} value={about} onChange={e => setAbout(e.target.value)}
          placeholder="Describe your company, products, and what you're looking for in a contractor..." />
      </div>
      <div>
        <label className={labelCls}>Website</label>
        <input className={inputCls} value={website} onChange={e => setWebsite(e.target.value)}
          placeholder="www.yourcompany.com" />
      </div>
    </div>
  );

  const stepShows = (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Where Do You Exhibit?</h2>
        <p className="text-gray-500 text-sm">
          Paketiniz: <span className="font-semibold text-indigo-600">{planCode}</span> — Kendi ülkeniz + en fazla <span className="font-semibold">{extraLimit}</span> ek ülke seçebilirsiniz.
        </p>
      </div>

      {/* Country search */}
      <div className="relative" ref={searchRef}>
        <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-3 bg-white focus-within:ring-2 focus-within:ring-indigo-500">
          <Search className="h-4 w-4 text-gray-400 shrink-0" />
          <input
            value={countryQuery}
            onChange={e => setCountryQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && countryDropdown.length > 0) {
                addCountry(countryDropdown[0]);
              }
            }}
            placeholder="Ülke adı yazın (en az 2 harf)..."
            className="flex-1 text-sm outline-none bg-transparent"
          />
        </div>
        {countryDropdown.length > 0 && (
          <div className="absolute z-20 w-full mt-1 bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden">
            {countryDropdown.map(c => (
              <button key={c.code} onClick={() => addCountry(c)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-indigo-50 transition-colors text-left">
                <span className="text-base">{c.flag || '🌍'}</span>
                <span className="font-medium text-gray-800">{c.name}</span>
                <span className="ml-auto text-xs text-gray-400">{c.code}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selected country tags */}
      {countries.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {countries.map(c => {
            const isOwn = c.code === homeCountryCode;
            return (
              <span key={c.code} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${
                isOwn ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-indigo-50 text-indigo-700 border-indigo-200'
              }`}>
                <span>{c.flag || '🌍'}</span>
                <span>{c.name}</span>
                {isOwn && <span className="text-indigo-300 text-xs">(kendi)</span>}
                {!isOwn && (
                  <button onClick={() => removeCountry(c.code)} className="ml-1 hover:opacity-70">
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}

      {/* Limit info */}
      {countries.length > 0 && (
        <p className="text-xs text-gray-400">
          {countries.filter(c => c.code !== homeCountryCode).length}/{extraLimit} ek ülke seçildi.
          {countries.filter(c => c.code !== homeCountryCode).length >= extraLimit && (
            <span className="text-amber-600 font-semibold ml-1">Limit doldu. Daha fazlası için planınızı yükseltebilirsiniz.</span>
          )}
        </p>
      )}

      {/* Cities section — one block per selected country */}
      {countries.length > 0 && (
        <div className="space-y-3">
          <label className={labelCls}>Şehir Seçimi</label>
          <p className="text-xs text-gray-400 -mt-1">Her ülke için katılım planladığınız şehirleri ekleyin. Ek ülkelerde 5 şehir ücretsiz, sonrası 10 kredi/şehir.</p>
          {countries.map(c => {
            const isHome = c.code === homeCountryCode;
            const selectedCities = citiesMap[c.code] || [];
            const extraCities = !isHome ? Math.max(0, selectedCities.length - FREE_CITY_LIMIT) : 0;
            const isLoading = cityLoading[c.code];
            const cityDropList = cityDropdowns[c.code] || [];
            const cityQ = cityQueries[c.code] || '';
            const isOpen = !!accordionOpen[c.code];

            return (
              <div key={c.code} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                {/* Accordion header — always visible */}
                <button
                  type="button"
                  onClick={() => setAccordionOpen(prev => ({ ...prev, [c.code]: !prev[c.code] }))}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-base">{c.flag || '🌍'}</span>
                    <span className="font-semibold text-gray-800 text-sm">{c.name}</span>
                    {isHome && (
                      <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">Kendi ülkeniz</span>
                    )}
                    {!isHome && (
                      <span className="text-xs text-gray-400">{FREE_CITY_LIMIT} ücretsiz · sonrası 10 kredi</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {isLoading && <div className="h-3.5 w-3.5 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />}
                    {selectedCities.length > 0 && (
                      <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                        {selectedCities.length} şehir
                      </span>
                    )}
                    {extraCities > 0 && (
                      <span className="text-xs text-amber-600 font-semibold bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                        {extraCities * 10} kredi
                      </span>
                    )}
                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                    {!isHome && (
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); removeCountry(c.code); }}
                        className="ml-1 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </button>

                {/* Accordion body */}
                {isOpen && (
                  <div className="px-4 pb-4 border-t border-gray-100">
                    {/* City search input */}
                    <div className="relative mt-3">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                      <input
                        value={cityQ}
                        onChange={e => searchCities(c.code, e.target.value)}
                        placeholder={`${c.name} şehri ara...`}
                        className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:bg-white transition"
                      />
                    </div>

                    {/* City dropdown */}
                    {cityDropList.length > 0 && (
                      <div className="mt-1 border border-gray-200 rounded-lg overflow-hidden shadow-sm max-h-44 overflow-y-auto">
                        {cityDropList.map(city => (
                          <button key={city.name} type="button" onClick={() => addCity(c.code, city.name)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 transition-colors border-b border-gray-50 last:border-0">
                            {city.name}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Selected cities tags */}
                    {selectedCities.length > 0 ? (
                      <div className={`flex flex-wrap gap-1.5 mt-2.5 ${isHome && selectedCities.length > 20 ? 'max-h-40 overflow-y-auto' : ''}`}>
                        {selectedCities.map((city, idx) => {
                          const isCreditCity = !isHome && idx >= FREE_CITY_LIMIT;
                          return (
                            <span key={city} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${
                              isCreditCity ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-gray-100 text-gray-700 border-gray-200'
                            }`}>
                              {city}
                              {isCreditCity && <span className="text-amber-500 text-[10px] font-bold">·10k</span>}
                              <button type="button" onClick={() => removeCity(c.code, city)} className="ml-0.5 hover:opacity-70">
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 mt-2">Henüz şehir eklenmedi. Yukarıdan arama yapın.</p>
                    )}

                    {/* Home country: quick-clear button */}
                    {isHome && selectedCities.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setCitiesMap(prev => ({ ...prev, [c.code]: [] }))}
                        className="mt-2 text-xs text-red-400 hover:text-red-600 underline"
                      >
                        Tümünü kaldır
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Industry tags */}
      <div>
        <label className={labelCls}>Industry Tags</label>
        <div className="flex flex-wrap gap-2">
          {INDUSTRY_OPTIONS.map(tag => {
            const sel = industry.includes(tag);
            return (
              <button key={tag} onClick={() => setIndustry(prev => sel ? prev.filter(t => t !== tag) : [...prev, tag])}
                className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-all ${sel
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-400'
                }`}>
                {sel && <Check className="h-3 w-3 inline mr-1" />}{tag}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  const stepBranding = (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Branding & Visuals</h2>
        <p className="text-gray-500 text-sm">Your logo and cover image appear on your public profile and briefs.</p>
      </div>
      <ImageUpload
        value={logoUrl}
        onChange={setLogoUrl}
        token={getToken()}
        label="Logo"
        hint="PNG, JPG, WEBP · Otomatik sıkıştırma"
        aspectRatio="logo"
        data-testid="logo-upload"
      />
      <ImageUpload
        value={coverUrl}
        onChange={setCoverUrl}
        token={getToken()}
        label="Kapak Görseli (opsiyonel)"
        hint="Önerilen: 1200×400px · Otomatik ölçekleme"
        aspectRatio="cover"
        data-testid="cover-upload"
      />
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>LinkedIn <span className="text-gray-400 font-normal">(optional)</span></label>
          <input className={inputCls} value={linkedin} onChange={e => setLinkedin(e.target.value)}
            placeholder="https://linkedin.com/company/..." />
        </div>
        <div>
          <label className={labelCls}>Instagram <span className="text-gray-400 font-normal">(optional)</span></label>
          <input className={inputCls} value={instagram} onChange={e => setInstagram(e.target.value)}
            placeholder="https://instagram.com/..." />
        </div>
      </div>
    </div>
  );

  const stepFinish = (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">All Set!</h2>
        <p className="text-gray-500 text-sm">Here's a summary of what you've set up. You can edit everything later.</p>
      </div>
      <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100 space-y-2">
        <p className="text-sm font-bold text-indigo-800 mb-2">Your Profile Summary</p>
        {[
          { label: 'Tagline',  value: tagline || '—',   ok: Boolean(tagline) },
          { label: 'About',    value: about ? 'Added' : '—', ok: Boolean(about) },
          { label: 'Website',  value: website || '—',   ok: Boolean(website) },
          { label: 'Countries', value: countries.length > 0 ? `${countries.length} ülke seçildi` : '—', ok: countries.length > 0 },
          { label: 'Cities', value: Object.values(citiesMap).flat().length > 0 ? `${Object.values(citiesMap).flat().length} şehir seçildi` : '—', ok: Object.values(citiesMap).flat().length > 0 },
          { label: 'Industry', value: industry.length > 0 ? industry.slice(0, 2).join(', ') : '—', ok: industry.length > 0 },
          { label: 'Logo',     value: logoUrl ? 'Set' : '—', ok: Boolean(logoUrl) },
        ].map(({ label, value, ok }) => (
          <div key={label} className="flex items-center justify-between text-sm">
            <span className="text-gray-600">{label}</span>
            <span className={`font-medium ${ok ? 'text-emerald-600' : 'text-gray-400'}`}>
              {ok && <Check className="h-3.5 w-3.5 inline mr-1" />}{value}
            </span>
          </div>
        ))}
      </div>
      <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
        <p className="text-sm text-blue-800 font-semibold mb-1">What's next?</p>
        <ul className="space-y-1.5 text-xs text-blue-700">
          <li>• Post your first brief to receive contractor proposals</li>
          <li>• Explore contractor portfolios and their match scores</li>
          <li>• Set up upcoming shows in your profile</li>
        </ul>
      </div>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 0: return stepWelcome;
      case 1: return stepCompany;
      case 2: return stepShows;
      case 3: return stepBranding;
      case 4: return stepFinish;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left Panel */}
      <div className="hidden lg:flex flex-col w-80 xl:w-96 bg-gradient-to-b from-indigo-900 to-indigo-700 p-8 text-white flex-shrink-0">
        <div className="flex items-center gap-3 mb-12">
          <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
            <Building2 className="h-4 w-4" />
          </div>
          <span className="font-bold text-lg">ExpoStand</span>
          <span className="text-indigo-300 text-sm ml-1">Exhibitor</span>
        </div>
        <div className="space-y-3 flex-1">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = i < step;
            const active = i === step;
            return (
              <div key={s.id} className={`flex items-center gap-3 p-3 rounded-xl transition-all ${active ? 'bg-white/15' : 'opacity-60'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all ${
                  done ? 'bg-emerald-400 border-emerald-400' : active ? 'bg-white/20 border-white' : 'border-white/40'
                }`}>
                  {done ? <Check className="h-4 w-4 text-white" /> : <Icon className="h-4 w-4" />}
                </div>
                <div>
                  <p className={`text-sm font-semibold ${active ? 'text-white' : 'text-white/80'}`}>{s.label}</p>
                  {active && <p className="text-xs text-indigo-300">Current step</p>}
                  {done && <p className="text-xs text-emerald-300">Completed</p>}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-8">
          <div className="flex justify-between text-xs text-indigo-300 mb-2">
            <span>Progress</span><span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-2">
            <div className="bg-white rounded-full h-2 transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* User info */}
        <div className="mt-6 pt-5 border-t border-white/20">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shrink-0 text-white font-bold text-sm">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="min-w-0">
              <p className="text-white text-xs font-semibold truncate">{companyName || 'Şirket Adı'}</p>
              <p className="text-indigo-200 text-xs truncate">{user?.name || '—'}</p>
              <p className="text-indigo-300 text-xs truncate">{user?.email || '—'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex flex-col">
        {/* Desktop Header */}
        <div className="hidden lg:flex items-center justify-between px-10 py-3.5 border-b bg-white shadow-sm">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Building2 className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold text-gray-900 text-sm">ExpoStand</span>
            <span className="text-gray-400 text-sm">·</span>
            <span className="text-indigo-600 text-sm font-medium">Exhibitor Onboarding</span>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-600 transition-colors"
            data-testid="onboarding-signout-btn"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>
        </div>
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b bg-white">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-indigo-600 flex items-center justify-center">
              <Building2 className="h-3.5 w-3.5 text-white" />
            </div>
            <span className="font-bold text-gray-900">ExpoStand Exhibitor</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={skipOnboarding} className="text-xs text-gray-400 hover:text-gray-600">Skip</button>
            <button onClick={logout} className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-600">
              <LogOut className="h-3.5 w-3.5" /> Sign Out
            </button>
          </div>
        </div>
        <div className="lg:hidden px-4 pt-3">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Step {step + 1} of {STEPS.length}</span><span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div className="bg-indigo-600 h-1.5 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-lg">
            {renderStep()}
            {error && <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{error}</div>}
          </div>
        </div>

        <div className="p-6 lg:px-12 border-t bg-white">
          <div className="max-w-lg mx-auto flex items-center justify-end gap-4">
            <div className="flex gap-3">
              {!isFirstStep && (
                <Button variant="outline" onClick={() => setStep(s => s - 1)} className="gap-2" data-testid="onboarding-back-btn">
                  <ChevronLeft className="h-4 w-4" /> Back
                </Button>
              )}
              {isFirstStep && (
                <Button variant="outline" onClick={skipOnboarding} className="text-gray-500" data-testid="onboarding-skip-btn">
                  Skip setup
                </Button>
              )}
              <Button onClick={handleNext} disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2 min-w-[120px]"
                data-testid="onboarding-next-btn">
                {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving...</>
                  : isLastStep ? <><Check className="h-4 w-4" /> Go to Dashboard</>
                  : <>Continue <ChevronRight className="h-4 w-4" /></>}
              </Button>
              {autoSaved && (
                <span className="text-xs text-green-600 font-medium flex items-center gap-1 animate-fade-in" data-testid="autosave-indicator">
                  <Check className="h-3 w-3" /> Kaydedildi
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
